// TODO: Fix domain package export
// import { validateTextSizeForLevel } from "@lingoshade/domain";
// TODO: Fix shared-utils package export
// import { clamp } from "@lingoshade/shared-utils";

// Temporary inline utility function
const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));
import {
  Upload as UploadIcon,
  PlayArrow as PlayIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  FormControlLabel,
  Checkbox,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../store";
import {
  bulkGenerateFromFile,
  fetchBulkGenerationStatus,
} from "../store/slices/adminTextsSlice";
import { SUPPORTED_LANGUAGES } from "../utils/languages";

interface BulkTextGenerationDialogProps {
  open: boolean;
  onClose: () => void;
}

// Преобразуем основной список в формат с флагами
const AVAILABLE_LANGUAGES = SUPPORTED_LANGUAGES.map((lang) => {
  const flagMap: Record<string, string> = {
    en: "🇺🇸",
    es: "🇪🇸",
    fr: "🇫🇷",
    de: "🇩🇪",
    it: "🇮🇹",
    ru: "��",
    pl: "��",
    zh: "🇨🇳",
    ja: "🇯🇵",
    ko: "🇰🇷",
    el: "🇬🇷",
    th: "��",
    tr: "🇹🇷",
    uk: "��",
  };

  return {
    code: lang.code,
    name: lang.name,
    flag: flagMap[lang.code] || "�️",
  };
});

const BulkTextGenerationDialog: React.FC<BulkTextGenerationDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { loading, error, bulkGenerationProgress } = useAppSelector(
    (state) => state.adminTexts,
  );

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  interface CsvRow {
    Level?: string;
    TextSize?: string;
    UserType?: string;
    ContentPrompt?: string;
    ExpectedTitle?: string;
    Notes?: string;
    [key: string]: string | undefined; // allow extra columns
  }
  const [csvPreview, setCsvPreview] = useState<CsvRow[]>([]);
  const [csvAllData, setCsvAllData] = useState<CsvRow[]>([]); // All данные CSV
  const [csvTotalRows, setCsvTotalRows] = useState(0); // Общее количество строк
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    AVAILABLE_LANGUAGES.map((lang) => lang.code), // Default все языки
  );
  const [checkDuplicates, setCheckDuplicates] = useState(true);
  const [maxTextsPerBatch, setMaxTextsPerBatch] = useState(50);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Polling для обновления статуса генерации
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (bulkGenerationProgress?.isActive && open) {
      // Запускаем опрос каждые 2 секунды
      interval = setInterval(() => {
        dispatch(fetchBulkGenerationStatus());
      }, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [bulkGenerationProgress?.isActive, open, dispatch]);

  // Автоматический переход к финальному шагу после завершения генерации (шаг 3 -> 4)
  useEffect(() => {
    if (
      bulkGenerationProgress &&
      !bulkGenerationProgress.isActive &&
      activeStep === 3
    ) {
      setActiveStep(4);
    }
  }, [bulkGenerationProgress, activeStep]);

  // Шаги мастера (добавлен явный шаг Generating)
  const steps = [
    "Upload CSV File",
    "Select Languages & Settings",
    "Review & Generate",
    "Generating",
    "Results",
  ];

  // Обработка выбора файла
  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      previewCSV(file);
    }
  };

  // Предварительный просмотр CSV
  const previewCSV = (file: File): void => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n").filter((line) => line.trim()); // убираем пустые строки

      if (lines.length === 0) return;

      // Более надёжный парсинг CSV с учётом кавычек
      const parseCSVLine = (line: string): string[] => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === "," && !inQuotes) {
            result.push(current.trim().replace(/^"|"$/g, ""));
            current = "";
          } else {
            current += char;
          }
        }

        result.push(current.trim().replace(/^"|"$/g, ""));
        return result;
      };

      const headers = parseCSVLine(lines[0]);

      // Подсчитываем общее количество строк данных (без заголовка)
      const totalDataRows = lines.length - 1;
      setCsvTotalRows(totalDataRows);
      setMaxTextsPerBatch(totalDataRows); // Устанавливаем по умолчанию = общему количеству

      // Парсим все данные
      const allData: CsvRow[] = lines
        .slice(1)
        .map((line) => {
          const values = parseCSVLine(line);
          return headers?.reduce<CsvRow>((obj, header, index) => {
            const key = header.trim();
            obj[key] = values[index]?.trim() || "";
            return obj;
          }, {} as CsvRow);
        })
        .filter((row) => row && Object.keys(row).length > 1);

      setCsvAllData(allData);

      // Validate CSV data using domain rules
      const validation = validateCsvData(allData);
      setValidationErrors(validation.errors);

      // Создаем preview первых 5 строк
      const preview = allData.slice(0, 5);
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  // Обработка выбора языков
  const handleLanguageToggle = (langCode: string): void => {
    setSelectedLanguages((prev) =>
      prev.includes(langCode)
        ? prev.filter((l) => l !== langCode)
        : [...prev, langCode],
    );
  };

  // Обработчики для быстрого выбора языков
  const handleSelectAllLanguages = (): void => {
    setSelectedLanguages(AVAILABLE_LANGUAGES.map((lang) => lang.code));
  };

  const handleClearAllLanguages = (): void => {
    setSelectedLanguages([]);
  };

  // Validate CSV data using domain rules
  const validateCsvData = (
    data: CsvRow[],
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because index is 0-based and there's a header

      // Level validation
      if (!row.Level?.trim()) {
        errors.push(`Row ${rowNumber}: Level is required`);
      }

      // TextSize validation
      if (!row.TextSize?.trim()) {
        errors.push(`Row ${rowNumber}: TextSize is required`);
      } else if (row.Level?.trim()) {
        // TODO: Fix domain package export and re-enable validation
        // Validate level-size compatibility using domain rules
        // const validation = validateTextSizeForLevel(
        //   row.Level.trim() as LevelCode,
        //   row.TextSize.trim() as TextSize,
        // );
        // if (!validation.isValid) {
        //   errors.push(
        //     `Row ${rowNumber}: ${row.TextSize} size is not available for ${row.Level} level. Available sizes: ${validation.allowedSizes.join(", ")}`,
        //   );
        // }
      }

      // ContentPrompt validation
      if (!row.ContentPrompt?.trim()) {
        errors.push(`Row ${rowNumber}: ContentPrompt is required`);
      } else if (row.ContentPrompt.trim().length < 10) {
        errors.push(
          `Row ${rowNumber}: ContentPrompt must be at least 10 characters long`,
        );
      } else if (row.ContentPrompt.trim().length > 1000) {
        errors.push(
          `Row ${rowNumber}: ContentPrompt cannot exceed 1000 characters`,
        );
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Валидация шагов
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return (
          selectedFile !== null &&
          csvPreview.length > 0 &&
          validationErrors.length === 0
        );
      case 1:
        return selectedLanguages.length > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  // Навигация по шагам
  const handleNext = (): void => {
    if (isStepValid(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = (): void => {
    setActiveStep((prev) => prev - 1);
  };

  // Запуск генерации
  const handleStartGeneration = async (): Promise<void> => {
    if (
      !selectedFile ||
      selectedLanguages.length === 0 ||
      csvAllData.length === 0
    )
      return;

    try {
      const result = await dispatch(
        bulkGenerateFromFile({
          csvData: csvAllData, // Отправляем все данные, а не только preview
          languages: selectedLanguages,
          checkDuplicates,
          maxTextsPerBatch,
        }),
      ).unwrap();

      // Если все тексты уже существуют -> сразу результаты, иначе переход на шаг генерации (3)
      if (result.allDuplicates === true || result.status === "completed") {
        setActiveStep(4);
      } else {
        setActiveStep(3);
      }
    } catch {
      // error state уже обновится через slice (thunk reject)
    }
  };

  // Сброс и закрытие
  const handleClose = (): void => {
    if (!loading && !bulkGenerationProgress?.isActive) {
      setActiveStep(0);
      setSelectedFile(null);
      setCsvPreview([]);
      setCsvAllData([]);
      setCsvTotalRows(0);
      setSelectedLanguages(AVAILABLE_LANGUAGES.map((lang) => lang.code)); // Сброс к "all languages"
      setCheckDuplicates(true);
      setMaxTextsPerBatch(50);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={loading || bulkGenerationProgress?.isActive}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon />
          {activeStep === 3 && bulkGenerationProgress?.isActive
            ? "Generation in Progress"
            : "Bulk Text Generation from File"}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Шаг 3: Прогресс генерации */}
        {activeStep === 3 && (
          <Box mb={3}>
            <Alert
              severity={bulkGenerationProgress?.isActive ? "info" : "success"}
              icon={<CircularProgress size={20} />}
            >
              <Typography variant="h6">
                {bulkGenerationProgress?.isActive
                  ? "Generation in Progress"
                  : "Finalizing..."}
              </Typography>
              {bulkGenerationProgress && (
                <>
                  <Typography>
                    Generated: {bulkGenerationProgress.generatedTexts} /{" "}
                    {bulkGenerationProgress.totalTexts}
                  </Typography>
                  {bulkGenerationProgress.currentLanguage &&
                    bulkGenerationProgress.isActive && (
                      <Typography variant="body2">
                        Current: {bulkGenerationProgress.currentLanguage} -{" "}
                        {bulkGenerationProgress.currentPrompt}
                      </Typography>
                    )}
                </>
              )}
            </Alert>
            {bulkGenerationProgress && (
              <LinearProgress
                variant="determinate"
                value={
                  (bulkGenerationProgress.generatedTexts /
                    Math.max(1, bulkGenerationProgress.totalTexts)) *
                  100
                }
                sx={{ mt: 2 }}
              />
            )}
            {bulkGenerationProgress?.errors &&
              bulkGenerationProgress.errors.length > 0 && (
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color="error">
                      {bulkGenerationProgress?.errors?.length} Errors
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {bulkGenerationProgress?.errors?.map((error, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <ErrorIcon color="error" />
                          </ListItemIcon>
                          <ListItemText primary={error} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}
            {bulkGenerationProgress?.skippedPrompts &&
              bulkGenerationProgress.skippedPrompts.length > 0 && (
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color="warning">
                      {bulkGenerationProgress.skippedPrompts.length} Skipped
                      (Duplicates)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {bulkGenerationProgress.skippedPrompts.map(
                        (skipped, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary={skipped.prompt}
                              secondary={`Duplicates found in: ${skipped.languages.join(", ")}`}
                            />
                          </ListItem>
                        ),
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}
          </Box>
        )}

        {/* Stepper: скрываем на финальном шаге результатов и во время активной генерации */}
        {activeStep !== 4 &&
          !(activeStep === 3 && bulkGenerationProgress?.isActive) && (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Шаг 0: Загрузка файла */}
        {activeStep === 0 && !bulkGenerationProgress?.isActive && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload CSV File
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
                mb: 2,
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon
                sx={{ fontSize: 48, color: "action.disabled", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                {selectedFile ? selectedFile.name : "Click to upload CSV file"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CSV file with columns: Level, TextSize, UserType, ContentPrompt,
                ExpectedTitle, Notes
              </Typography>
            </Paper>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={handleFileSelect}
            />

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  CSV Validation Errors ({validationErrors.length})
                </Typography>
                <List dense>
                  {validationErrors.slice(0, 10).map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
                {validationErrors.length > 10 && (
                  <Typography variant="body2" color="textSecondary">
                    ... and {validationErrors.length - 10} more errors
                  </Typography>
                )}
              </Alert>
            )}

            {/* Предварительный просмотр */}
            {csvPreview.length > 0 && (
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Preview (first 5 rows)</Typography>
                  <Chip
                    label={`Total: ${csvTotalRows} rows`}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Paper sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
                  {csvPreview.map((row, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        pb: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="subtitle2">
                        {row.Level} - {row.TextSize} - {row.UserType}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {row.ContentPrompt?.substring(0, 100)}...
                      </Typography>
                      <Chip
                        label={row.ExpectedTitle}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ))}
                </Paper>
                <Typography variant="caption" color="text.secondary">
                  Found {csvTotalRows} rows total in CSV file
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Шаг 2: Выбор языков и настроек */}
        {activeStep === 1 && !bulkGenerationProgress?.isActive && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Languages & Settings
            </Typography>

            {/* Выбор языков */}
            <Box mb={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1">Target Languages *</Typography>
                <Box>
                  <Button
                    size="small"
                    onClick={handleSelectAllLanguages}
                    disabled={
                      selectedLanguages.length === AVAILABLE_LANGUAGES.length
                    }
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    onClick={handleClearAllLanguages}
                    sx={{ ml: 1 }}
                    disabled={selectedLanguages.length === 0}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1}>
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <Chip
                    key={lang.code}
                    label={`${lang.flag} ${lang.name}`}
                    clickable
                    variant={
                      selectedLanguages.includes(lang.code)
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() => handleLanguageToggle(lang.code)}
                    color={
                      selectedLanguages.includes(lang.code)
                        ? "primary"
                        : "default"
                    }
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Selected: {selectedLanguages.length} of{" "}
                {AVAILABLE_LANGUAGES.length} languages
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Настройки генерации */}
            <Typography variant="h6" gutterBottom>
              Generation Settings
            </Typography>

            <Grid container spacing={3}>
              {/* Number of rows to process */}
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  label="Number of rows to process"
                  type="number"
                  value={maxTextsPerBatch}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || csvTotalRows;
                    setMaxTextsPerBatch(clamp(value, 1, csvTotalRows));
                  }}
                  inputProps={{ min: 1, max: csvTotalRows }}
                  helperText={
                    csvTotalRows > 0
                      ? `Of ${csvTotalRows} total rows in the file. Leave ${csvTotalRows} to process all.`
                      : "Number of rows from the CSV file for text generation"
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <Box sx={{ pt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setMaxTextsPerBatch(csvTotalRows)}
                    disabled={maxTextsPerBatch === csvTotalRows}
                    fullWidth
                  >
                    All rows ({csvTotalRows})
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Информация о CSV данных */}
            <Box mt={3}>
              <Alert severity="info">
                <Typography variant="subtitle2">
                  About your CSV file:
                </Typography>
                <Typography variant="body2">
                  • Found <strong>{csvTotalRows} rows</strong> with prompts for
                  generation
                  <br />
                  • Levels, text sizes and user types will be taken from CSV
                  <br />• You can set the number of rows to process (all by
                  default: {csvTotalRows})<br />• For each row, texts will be
                  generated in the selected languages
                </Typography>
              </Alert>
            </Box>

            {/* Опции */}
            <Box mt={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkDuplicates}
                    onChange={(e) => setCheckDuplicates(e.target.checked)}
                  />
                }
                label="Check for duplicates (skip texts with similar content already in database)"
              />
            </Box>
          </Box>
        )}

        {/* Шаг 3: Обзор и запуск */}
        {activeStep === 2 && !bulkGenerationProgress?.isActive && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Start Generation
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Selected File:</Typography>
                  <Typography>{selectedFile?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {csvTotalRows} rows total, processing {maxTextsPerBatch}{" "}
                    rows
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Target Languages:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {selectedLanguages.map((langCode) => {
                      const lang = AVAILABLE_LANGUAGES.find(
                        (l) => l.code === langCode,
                      );
                      return (
                        <Chip
                          key={langCode}
                          label={`${lang?.flag} ${lang?.name}`}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Rows to Process:</Typography>
                  <Typography>
                    {maxTextsPerBatch} of {csvTotalRows}
                  </Typography>
                  {maxTextsPerBatch < csvTotalRows && (
                    <Typography variant="caption" color="warning.main">
                      Only the first {maxTextsPerBatch} rows will be processed
                    </Typography>
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Duplicate Check:</Typography>
                  <Typography>
                    {checkDuplicates ? "Enabled" : "Disabled"}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" color="primary">
                    Will be generated: {maxTextsPerBatch} ×{" "}
                    {selectedLanguages.length} ={" "}
                    {maxTextsPerBatch * selectedLanguages.length} texts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {maxTextsPerBatch} rows from CSV ×{" "}
                    {selectedLanguages.length} selected languages.
                    {checkDuplicates && " Duplicates will be skipped."}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="subtitle2">Generation Process:</Typography>
              <Typography variant="body2">
                • Each prompt from your CSV will generate texts for all selected
                languages
                <br />
                • Levels, text sizes, and user access will be taken directly
                from the CSV
                <br />
                • Generation runs in the background with automatic rate limiting
                <br />
                • You can monitor progress and see any errors in real-time
                <br />• Generated texts will appear in the main texts list when
                completed
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Шаг 4: Результаты */}
        {activeStep === 4 && !bulkGenerationProgress?.isActive && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generation Results
            </Typography>

            {bulkGenerationProgress && (
              <Box>
                <Paper sx={{ p: 3, mb: 2 }}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box textAlign="center">
                        <CheckCircleIcon
                          sx={{ fontSize: 40, color: "success.main", mb: 1 }}
                        />
                        <Typography variant="h4" color="success.main">
                          {bulkGenerationProgress.generatedTexts || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Successfully generated
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box textAlign="center">
                        <ErrorIcon
                          sx={{ fontSize: 40, color: "error.main", mb: 1 }}
                        />
                        <Typography variant="h4" color="error.main">
                          {bulkGenerationProgress.errors.length || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Generation errors
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box textAlign="center">
                        <WarningIcon
                          sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
                        />
                        <Typography variant="h4" color="warning.main">
                          {bulkGenerationProgress.skippedPrompts
                            ? bulkGenerationProgress.skippedPrompts.reduce(
                                (total, item) => total + item.languages.length,
                                0,
                              )
                            : 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Skipped texts (duplicates)
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box textAlign="center">
                        <InfoIcon
                          sx={{ fontSize: 40, color: "info.main", mb: 1 }}
                        />
                        <Typography variant="h4" color="info.main">
                          {bulkGenerationProgress.totalTexts || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total scheduled
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Детали ошибок */}
                {bulkGenerationProgress.errors &&
                  bulkGenerationProgress.errors.length > 0 && (
                    <Accordion sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography color="error">
                          {bulkGenerationProgress.errors.length} Errors Details
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {bulkGenerationProgress.errors.map((error, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <ErrorIcon color="error" />
                              </ListItemIcon>
                              <ListItemText primary={error} />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                {/* Детали пропущенных */}
                {bulkGenerationProgress.skippedPrompts &&
                  bulkGenerationProgress.skippedPrompts.length > 0 && (
                    <Accordion sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography color="warning">
                          {bulkGenerationProgress.skippedPrompts.reduce(
                            (total, item) => total + item.languages.length,
                            0,
                          )}{" "}
                          Skipped Texts Details
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {bulkGenerationProgress.skippedPrompts.map(
                            (skipped, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <WarningIcon color="warning" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={skipped.prompt}
                                  secondary={`Duplicates found in ${skipped.languages.length} languages: ${skipped.languages.join(", ")}`}
                                />
                              </ListItem>
                            ),
                          )}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}

                {bulkGenerationProgress.generatedTexts === 0 &&
                bulkGenerationProgress.errors.length === 0 &&
                bulkGenerationProgress.skippedPrompts &&
                bulkGenerationProgress.skippedPrompts.reduce(
                  (total, item) => total + item.languages.length,
                  0,
                ) > 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      All texts from your CSV file already exist in the
                      database! Nothing was generated because duplicate checking
                      is enabled. If you want to create new texts, try other
                      prompts or disable duplicate checking.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Generation complete! Created texts are now available in
                      the main text list. You can close this window or start a
                      new generation.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {/* Скрываем все кнопки во время активной генерации */}
        {!(activeStep === 3 && bulkGenerationProgress?.isActive) && (
          <>
            {activeStep === 4 ? (
              // Финальный шаг - только кнопки Close и New Generation
              <>
                <Button onClick={handleClose} variant="contained">
                  Close
                </Button>
                <Button
                  onClick={() => setActiveStep(0)}
                  variant="outlined"
                  startIcon={<AddIcon />}
                >
                  New Generation
                </Button>
              </>
            ) : (
              // Обычная навигация по шагам
              <>
                <Button onClick={handleClose} disabled={loading}>
                  Cancel
                </Button>

                {activeStep > 0 && (
                  <Button onClick={handleBack} disabled={loading}>
                    Back
                  </Button>
                )}

                {activeStep < 2 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!isStepValid(activeStep) || loading}
                    variant="contained"
                  >
                    Next
                  </Button>
                ) : activeStep === 2 ? (
                  <Button
                    onClick={handleStartGeneration}
                    disabled={loading || selectedLanguages.length === 0}
                    variant="contained"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <PlayIcon />
                    }
                  >
                    {loading ? "Starting..." : "Start Generation"}
                  </Button>
                ) : null}
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkTextGenerationDialog;
